import { PagedApiResponse, Pagination } from '@oncolens/oncolens-ui/lib/dto/api/ApiResponse';
import { useQuery } from 'react-query';
import client from '@/utils/httpClient';

const keys = {
  baseKey: 'panelOrders' as const,
  all: ['panelOrders'] as const,
  list: () => [...keys.all, 'list'],
  detail: (id: string) => [...keys.all, id]
};

export function getPanelOrders(queryString: string) {
  return client.get(`/api/panelorders/worklist?${queryString}`).then((response) => {
    const data = response.data as PanelOrderDto[];

    return {
      data,
      pagination: JSON.parse(response.headers['x-pagination']) as Pagination
    } as PagedApiResponse<PanelOrderDto>;
  });
}

export function getPanelOrderDetails(id: string): Promise<PanelOrderTestResultsSummaryDto> {
  return client
    .get(`/api/panelorders/${id}/results`)
    .then((response) => response.data as PanelOrderTestResultsSummaryDto);
}

export function usePanelOrderDetails(id: string) {
  return useQuery(keys.detail(id), () => getPanelOrderDetails(id), {
    retry: false
  });
}

export enum PanelOrderReportType {
	Final = 'Final',
	Qns = 'QNS',
	Amended = 'Amended',
	Addended = 'Addended',
	Corrected = 'Corrected'
}

export interface PanelOrderTestResultsSummaryDto {
	panelOrder: PanelOrderSummaryDto;
	testResults: TestResultSummaryDto[];
	potentialPatientMatches?: PatientMatchesDto[];
	relatedPanelOrders?: PanelOrderDto[];
	patientAssignment?: AssignedPatientDto;
}

export interface PatientMatchesDto {
	patientId: string;
	fullName: string;
	dateOfBirth: Date;
	age: number;
	sex: string;
	ethnicity: string;
	race: string;
	events: MatchConferenceDto[];
}

export interface MatchConferenceDto {
	tumorboardMeetingName: string;
	assignedDate: Date;
}
export interface AssignedPatientDto {
	id: string;
	name: string;
}

export type PanelOrderSummaryDto = PanelOrderSummaryPatientDto & {
	id: string;
	orderDate: Date;
	reportDate: Date;
	reportingLab: string;
	panelName: string;
	oledDocumentId: string;
	patientId: string;
	pdfUrl: string;
	panelType: string;
	accessionNumber: string;
	collectionSite: string;
	diagnosis: string;
	organizationId: string;
	amendmentInformation?: AmendmentInformationDto;
};

export interface AmendmentInformationDto {
	amendmentType: PanelOrderReportType;
	amendmentComments?: string;
	amendedLabReportId?: string;
	amendedBy?: string;
	amendedDate?: Date;
}

export interface TestResultSummaryDto {
	id: string;
	biomarkerName: string;
	interpretation: string;
	testMethodology: string;
	analyte: string;
	result: string;
	resultDisplayAttributes: ResultDisplayAttributesSummaryDto;
	therapyAssociations: TherapyAssociationSummaryDto[];
}

export enum TherapyAssociationRecommendedSensitivity {
	Benefit = 'Benefit',
	LackOfBenefit = 'Lack Of Benefit'
}

export interface TherapyAssociationSummaryDto {
	therapyName: string;
	recommendedSensitivity: TherapyAssociationRecommendedSensitivity;
	level: string;
}

export interface ResultDisplayAttributesSummaryDto {
	alleleFrequency: string;
	chromosome: string;
	dbVarId: string;
	equivocal?: boolean;
	exon: string;
	genomeBuild: string;
	genomicSource: string;
	hgvsCodingChange: string;
	hgvsProteinChange: string;
	molecularConsequence: string;
	readInformation: string;
	unknownSignificance: string;
	wildtypeBiomarker: string;
	expressed?: boolean;
}

export interface PanelOrderSummaryPatientDto {
	patientFirstName: string;
	patientLastName: string;
	patientName: string;
	patientMrn: string;
	patientDateOfBirth: Date;
	patientAge: number;
	patientSex: string;
	patientSexId: string;
	patientRace: string;
	patientEthnicity: string;
}

export interface PanelOrderDto {
	id: string;
	orderingProviderNpi: string;
	orderingProviderName: string;
	orderingProviderFirstName: string;
	orderingProviderLastName: string;
	orderDate: Date;
	reportDate: Date;
	patientName: string;
	patientDateOfBirth: Date;
	panelName: string;
	collectionSite: string;
	accessionNumber: string;
	status: string;
	reportType: PanelOrderReportType;
	organizationId: string;
	patientId?: string;
}

export { keys as panelOrderKeys };
